import React, { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import MoneyInput from '../../shared/MoneyInput';
import NumberInput from '../../shared/NumberInput';
import PickSelect from '../../shared/PickSelect';
import { bakeoffYearOptions } from '../constants';
import { cashFlowYears, newSection } from './useIncomeForms';
import { MissingMark, MissingPanel, SaveBar, VersionLine, Warnings, money, pct } from './formParts';

// One renderer for Form 1084 and Form 91: both are repeatable sections of
// labelled lines with a sign, one input per tax year (ticket 215).
const SIGN_BADGE = { add: '(+)', subtract: '(−)', signed: '(+/−)', reverse: '(+/−)', miles: 'miles' };
const METHOD_LABEL = { average: 'Average both years (÷ 24)', most_recent: 'Most recent year (÷ 12)' };
const METHOD_OPTIONS = [
  { value: '', label: 'Default (chosen from the trend)' },
  { value: 'average', label: METHOD_LABEL.average },
  { value: 'most_recent', label: METHOD_LABEL.most_recent },
];
const CELL = 'w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-brand-navy';

// missing_fields -> a set of keys for the inputs to outline.
function missingKeys(result) {
  const keys = new Set();
  (result?.missing_fields || []).forEach((m) => {
    const line = String(m.line ?? '');
    if (line === 'owns_100_percent') keys.add(`${m.section_index}|own100`);
    else if (line === 'ownership_pct') keys.add(`${m.section_index}|own|${m.year ?? '*'}`);
    // "2a or 2b": an at-least-one-of rule outlines every line it names.
    else line.split(' or ').forEach((k) => keys.add(`${m.section_index}|${k}|${m.year ?? '*'}`));
  });
  return keys;
}

function YearHeader({ years }) {
  return (
    <div className="grid gap-2 mb-1" style={{ gridTemplateColumns: `minmax(0,1fr) repeat(${years.length}, 9rem)` }}>
      <div />
      {years.map((y) => (
        <div key={y} className="text-[11px] uppercase tracking-wide font-bold text-slate-500 text-right" data-testid={`income-year-head-${y}`}>{y}</div>
      ))}
    </div>
  );
}

function SectionCard({ idx, section, sdef, def, years, slots, missing, onChange, onRemove }) {
  const hit = (line, year) => missing.has(`${idx}|${line}|${year}`) || missing.has(`${idx}|${line}|*`);
  const setValue = (key, slot, n) => onChange((s) => ({ ...s, values: { ...s.values, [key]: { ...s.values[key], [slot]: n } } }));
  const cols = { gridTemplateColumns: `minmax(0,1fr) repeat(${years.length}, 9rem)` };
  const refOf = (k) => sdef.lines.find((l) => l.key === k)?.ref || k;
  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 space-y-3" data-testid={`income-section-${idx}`}>
      <div className="flex items-center gap-3">
        <div className="text-[11px] uppercase tracking-wide font-bold text-slate-600 flex-1" data-testid={`income-section-title-${idx}`}>
          {sdef.number ? `${sdef.number}. ` : ''}{sdef.title}
        </div>
        {sdef.named && (
          <Input
            value={section.name}
            onChange={(e) => onChange((s) => ({ ...s, name: e.target.value }))}
            placeholder={sdef.name_label}
            aria-label={sdef.name_label}
            maxLength={120}
            className="h-8 w-56 text-sm"
            data-testid={`income-section-name-${idx}`}
          />
        )}
        <button type="button" onClick={onRemove} className="p-1.5 hover:bg-red-50 rounded-lg" aria-label="Remove section" data-testid={`income-section-remove-${idx}`}>
          <X size={14} className="text-red-500" />
        </button>
      </div>

      {sdef.ownership === 'hundred_checkbox' && (
        <label className={`flex items-center gap-2 text-sm ${missing.has(`${idx}|own100`) ? MISSING_RING : ''}`} data-testid={missing.has(`${idx}|own100`) ? 'missing-input' : undefined}>
          <input
            type="checkbox"
            checked={!!section.owns100}
            onChange={(e) => onChange((s) => ({ ...s, owns100: e.target.checked }))}
            data-testid={`income-owns100-${idx}`}
          />
          Borrower(s) own 100% of the corporation
        </label>
      )}
      {sdef.required_any_of && (
        <div className="text-[11px] text-slate-500">
          Enter at least one of: {sdef.required_any_of.map(refOf).join(', ')}.
        </div>
      )}

      <div>
        <YearHeader years={years} />
        {sdef.lines.map((l) => (
          <div key={l.key} className="grid gap-2 items-start py-1.5 border-t border-slate-200" style={cols} data-testid={`income-line-row-${idx}-${l.key}`}>
            <div className="min-w-0">
              <div className="text-sm text-slate-800">
                {l.ref && <span className="font-mono text-xs text-slate-500 mr-1.5">{l.ref}</span>}
                {l.label}
                {l.required && <span className="text-amber-700 ml-1" title="Required">*</span>}
                <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700" data-testid={`income-sign-${idx}-${l.key}`}>{SIGN_BADGE[l.kind] || ''}</span>
              </div>
              {(l.hint || l.source) && (
                <div className="text-[11px] text-slate-500">{[l.hint, l.source].filter(Boolean).join(' · ')}</div>
              )}
              {l.entry_note && (
                <div className="text-[11px] text-slate-500" data-testid={`income-entry-note-${idx}-${l.key}`}>{l.entry_note}</div>
              )}
              {l.kind === 'miles' && (
                <div className="text-[11px] text-slate-500">
                  Rate per mile: {years.map((y) => `${y} ${def.mileage_rates?.[String(y)] != null ? `$${def.mileage_rates[String(y)]}` : 'not available'}`).join(' · ')}
                </div>
              )}
            </div>
            {slots.map((sl, i) => {
              const Input_ = l.kind === 'miles' ? NumberInput : MoneyInput;
              return (
                <MissingMark key={sl} missing={hit(l.key, years[i])}>
                  <Input_
                    value={section.values[l.key]?.[sl] ?? null}
                    onChange={(n) => setValue(l.key, sl, n)}
                    placeholder=""
                    aria-label={`${l.label} ${years[i]}`}
                    className={CELL}
                    data-testid={`income-line-${idx}-${l.key}-${years[i]}`}
                  />
                </MissingMark>
              );
            })}
          </div>
        ))}
        {sdef.ownership === 'percent' && (
          <div className="grid gap-2 items-start py-1.5 border-t border-slate-200" style={cols}>
            <div className="text-sm text-slate-800">Ownership % <span className="text-amber-700" title="Required when any adjustment is entered">*</span></div>
            {slots.map((sl, i) => (
              <MissingMark key={sl} missing={missing.has(`${idx}|own|${years[i]}`) || missing.has(`${idx}|own|*`)}>
                <NumberInput
                  value={section.ownership[sl] ?? null}
                  onChange={(n) => onChange((s) => ({ ...s, ownership: { ...s.ownership, [sl]: n } }))}
                  min={0}
                  max={100}
                  suffix="%"
                  aria-label={`Ownership % ${years[i]}`}
                  className={CELL}
                  data-testid={`income-ownership-${idx}-${years[i]}`}
                />
              </MissingMark>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
// Ownership is stored to 4 decimals: show 25%, 33.3333%, never money.
const ownershipPct = (n) => (n == null ? '—' : `${Number(n)}%`);
const MISSING_RING = 'rounded-md ring-2 ring-amber-300 px-1';

function ResultPanel({ def, result }) {
  const years = result.years || [];
  const grid = { gridTemplateColumns: `minmax(0,1fr) repeat(${years.length}, 9rem)` };
  const methodDef = result.method;
  return (
    <div className="space-y-3" data-testid="income-form-result">
      {result.qualifying_monthly == null && <MissingPanel result={result} />}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="grid gap-2 px-3 py-2 bg-slate-50 text-[11px] uppercase tracking-wide font-bold text-slate-500" style={grid}>
          <div>Section</div>
          {years.map((y) => <div key={y} className="text-right">{y}</div>)}
        </div>
        {(result.sections || []).map((rs, i) => {
          const sdef = def.sections.find((s) => s.type === rs.type);
          return (
            <div key={i} className="px-3 py-2 border-t border-slate-100 text-sm" data-testid={`income-result-section-${i}`}>
              <div className="font-semibold text-slate-800">{rs.number ? `${rs.number}. ` : ''}{rs.title}{rs.name ? ` — ${rs.name}` : ''}</div>
              {(sdef?.computed || []).map((c) => (
                <div key={c.key} className="grid gap-2 text-slate-600" style={grid}>
                  <div>{c.ref ? `${c.ref} ` : ''}{c.label}</div>
                  {years.map((y, yi) => <div key={y} className="text-right tabular-nums">{c.key === 'ownership_pct' ? ownershipPct(rs.computed?.[c.key]?.[yi]) : money(rs.computed?.[c.key]?.[yi])}</div>)}
                </div>
              ))}
              <div className="grid gap-2 font-semibold text-slate-800" style={grid}>
                <div>Subtotal</div>
                {years.map((y, yi) => <div key={y} className="text-right tabular-nums" data-testid={`income-subtotal-${i}-${y}`}>{money(rs.subtotal?.[yi])}</div>)}
              </div>
            </div>
          );
        })}
        <div className="grid gap-2 px-3 py-2 border-t border-slate-200 bg-slate-50 text-sm font-bold text-slate-800" style={grid}>
          <div>Year total</div>
          {years.map((y, yi) => <div key={y} className="text-right tabular-nums" data-testid={`income-year-total-${y}`}>{money(result.year_totals?.[yi])}</div>)}
        </div>
      </div>
      {result.trend && (
        <div className="text-xs text-slate-600" data-testid="income-form-trend">
          Change between years: {money(result.trend.change)} ({pct(result.trend.change_pct)}){result.trend.declining ? ' — declining' : ''}
        </div>
      )}
      {result.qualifying_monthly != null && (
        <div className="rounded-lg px-4 py-3 bg-emerald-50 border border-emerald-200" data-testid="income-form-summary">
          <div className="text-[11px] uppercase tracking-wide font-bold text-emerald-800">Qualifying income summary (not part of the agency form)</div>
          {methodDef && (
            <div className="text-xs text-emerald-900 mt-1" data-testid="income-form-method">
              Method: {METHOD_LABEL[methodDef.selected] || methodDef.selected} · {methodDef.months} months · total used {money(methodDef.total_used)}
              {methodDef.default && methodDef.default !== methodDef.selected && ` (default would be ${METHOD_LABEL[methodDef.default] || methodDef.default})`}
            </div>
          )}
          <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="income-form-qualifying">{money(result.qualifying_monthly)}/mo</div>
        </div>
      )}
      <Warnings warnings={result.warnings} />
    </div>
  );
}

export function CashFlowForm({ formId, def, ctl }) {
  const f = ctl.forms[formId];
  const d = f.draft;
  const [menuOpen, setMenuOpen] = useState(false);
  const years = cashFlowYears(d);
  const slots = d.yearCount === 2 ? ['prior', 'recent'] : ['recent'];
  const missing = useMemo(() => missingKeys(f.result), [f.result]);

  const yearOptions = useMemo(() => {
    const opts = bakeoffYearOptions();
    if (!opts.some((o) => o.value === String(d.recentYear))) {
      opts.push({ value: String(d.recentYear), label: String(d.recentYear) });
      opts.sort((a, b) => Number(b.value) - Number(a.value));
    }
    return opts;
  }, [d.recentYear]);

  const up = (fn) => ctl.update(formId, fn);
  const setSection = (idx, fn) => up((dr) => ({ ...dr, sections: dr.sections.map((s, i) => (i === idx ? fn(s) : s)) }));
  const addSection = (type) => {
    up((dr) => ({ ...dr, sections: [...dr.sections, newSection(type)] }));
    setMenuOpen(false);
  };
  const canAdd = (s) => s.repeatable || !d.sections.some((x) => x.type === s.type);
  const canCalc = !f.busy && d.sections.length > 0;
  const preview = f.result;

  return (
    <div className="space-y-4" data-testid={`income-form-${formId}`}>
      <VersionLine def={def} />

      <div className="flex flex-wrap items-end gap-3">
        <span className="text-xs font-semibold text-slate-600 pb-2">Tax Years:</span>
        {[1, 2].map((n) => (
          <button
            key={n}
            onClick={() => d.yearCount !== n && up((dr) => ({ ...dr, yearCount: n, method: n === 1 ? null : dr.method }))}
            className={`px-3 py-1 rounded-md text-xs font-semibold mb-1 ${d.yearCount === n ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-600'}`}
            data-testid={`income-years-${n}`}
          >{n} {n === 1 ? 'Year' : 'Years'}</button>
        ))}
        <div className="w-28">
          <PickSelect
            label={d.yearCount === 2 ? 'Most recent year' : 'Tax year'}
            value={String(d.recentYear)}
            onChange={(v) => up((dr) => ({ ...dr, recentYear: Number(v) }))}
            options={yearOptions}
            testId="income-year-recent"
          />
        </div>
      </div>

      {d.sections.map((s, idx) => {
        const sdef = def.sections.find((x) => x.type === s.type);
        return (
          <SectionCard
            key={s.uid}
            idx={idx}
            section={s}
            sdef={sdef}
            def={def}
            years={years}
            slots={slots}
            missing={missing}
            onChange={(fn) => setSection(idx, fn)}
            onRemove={() => up((dr) => ({ ...dr, sections: dr.sections.filter((_, i) => i !== idx) }))}
          />
        );
      })}

      <div>
        <Button variant="outline" onClick={() => setMenuOpen(!menuOpen)} data-testid="income-add-section">
          <Plus size={14} /> Add section
        </Button>
        {menuOpen && (
          <div className="mt-2 rounded-lg border border-slate-200 bg-white p-1 flex flex-col max-w-md" data-testid="income-add-section-menu">
            {def.sections.map((s) => (
              <button
                key={s.type}
                type="button"
                disabled={!canAdd(s)}
                onClick={() => addSection(s.type)}
                className="text-left text-sm px-3 py-1.5 rounded hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                data-testid={`income-add-${s.type}`}
              >
                {s.number ? `${s.number}. ` : ''}{s.title}{!s.repeatable && !canAdd(s) ? ' (added)' : ''}
              </button>
            ))}
          </div>
        )}
      </div>

      {d.yearCount === 2 && (
        <div className="max-w-sm space-y-1">
          <PickSelect
            label="Method"
            value={d.method || ''}
            onChange={(v) => up((dr) => ({ ...dr, method: v || null }))}
            options={METHOD_OPTIONS}
            testId="income-method"
          />
          <div className="text-[11px] text-slate-500" data-testid="income-method-note">
            {preview?.method
              ? `Default for these figures: ${METHOD_LABEL[preview.method.default] || preview.method.default}.`
              : 'The default is the average, or the most recent year when income declined.'}
          </div>
        </div>
      )}

      <Button onClick={() => ctl.calculate(formId)} disabled={!canCalc} variant="navy" className="w-full" data-testid="income-calculate">
        {f.busy ? 'Working…' : 'Calculate'}
      </Button>
      {d.sections.length === 0 && (
        <div className="text-xs text-slate-500" data-testid="income-form-hint">Add a section to start. Leave a line empty if the return shows nothing on it.</div>
      )}

      {f.result && <ResultPanel def={def} result={f.result} />}

      <SaveBar formId={formId} ctl={ctl} />
    </div>
  );
}
